import React, {useEffect, useMemo} from "react";
import {Navigate, RouteProps, useLocation} from "react-router-dom";
import SignInPage from "./components/signIn/SignInPage";
import {appPagePaths, appRoutePaths, currentSignInDataVersion} from "./config/config";
import {routeChanged} from "./services/eventBus/route/routeEventBus";
import nativeBridge from "./services/nativeBridge";
import {setItem} from "./services/device/localStorage";
import onboarding from "./services/onboarding/onboarding";
import {logger} from "./services/log/logger";
import {getSignInDataFromLocalStorage} from "./services/user/sign-in-data";
import {localStorageService} from "./services/storage/local-storage";
import storageKeys from "./services/storage/storageKeys";
import {deviceStore} from "./redux/device/deviceStore";
import {PDCStatus} from "./redux/types/pdcStatus";

function AppRoute(props: RouteProps) {
  const location = useLocation();

  const computedComponent = useMemo(() => {
    // Ensure that the element is a valid React element; if not, fallback to <SignInPage />
    // This conditional logic is inside the useMemo callback to prevent unnecessary re-renders.
    const elementToRender = React.isValidElement(props.element)
      ? props.element
      : <SignInPage />;

    const component = determineComponentFromRoutePath(
      elementToRender,
      props.path ?? ""
    );

    return component;
  }, [props.element, props.path]);

  useEffect(() => {
    executeSideEffects(
      props.path as string,
      location.pathname,
      location.search
    );
  }, [props.path, location.pathname, location.search]);

  return computedComponent;
}

/**
 * Pure function to determine what component to render.
 *
 * Redirect to a different route if appropriate
 * (e.g. Redirect to sign in if data isn't found in local storage.)
 *  @param Component - component to render if sign in isn't required.
 *  @param routePath - path route uses to match. e.g. "/lesson/{lessonId}/{segmentId};
 */
function determineComponentFromRoutePath(
  Component: React.ReactElement,
  routePath: string,
): React.ReactElement {
  // Section: Prominent disclosure and consent redirect
  const isAndroid = deviceStore.getDeviceOsIsAndroid();
  const pdcStatus = localStorageService.getJsonItem<PDCStatus>(storageKeys.consent_PDCStatus) ?? "unknown";
  const { isRouteValid, lastLocationPathUsed } = getValidLastLocationPathUsed();

  if (isAndroid && pdcStatus === "unknown" && routePath !== appRoutePaths.onboarding_disclosureAndConsent) {
    const toPathRoot = appRoutePaths.onboarding_disclosureAndConsent;
    const toPath = isRouteValid ? `${toPathRoot}?from=${lastLocationPathUsed}` : toPathRoot;

    return <Navigate replace to={toPath} />;
  }

  const pathToCheck = routePath === appRoutePaths.lastPageUsed
    ? lastLocationPathUsed
    : routePath;

  // Section: Onboarding redirect
  if (!onboarding.getIsOnboardingCompleted() && (!pathToCheck || !isRoutePathOnboardingRelated(pathToCheck))) {
    return <Navigate replace to={appPagePaths.infoPage} />;
  }

  // Section: Login required redirect
  if (isSignInNeededToViewPage(routePath)) {
    return <Navigate to={appRoutePaths.signInPage} />;
  }

  let isRouteInvalid = false;

  // Section: When the route is "last-page-used", look up the last page used from local storage and redirect to that page if it is valid
  if (routePath === appRoutePaths.lastPageUsed) {
    const { isRouteValid, lastLocationPathUsed } = getValidLastLocationPathUsed();
    isRouteInvalid = !isRouteValid;

    if (isRouteValid) {
      return <Navigate replace to={lastLocationPathUsed} />;
    }
  }

  // Section: Invalid path redirect
  if (isRouteInvalid) {
    return <Navigate to={appPagePaths.bootCampPage} />;
  }

  // Section: Root path redirect
  if (routePath === "/") {
    return <Navigate replace to={appRoutePaths.dashboardPage} />;
  }

  return Component;
}

type LastLocationPathResult = {
  isRouteValid: true;
  lastLocationPathUsed: string;
} | {
  isRouteValid: false;
  lastLocationPathUsed?: undefined;
}

/**
 * Looks up the last location path used from local storage, and checks if it is valid.
 * @returns - object indicating if the route is valid, and the last location path used if it is valid.
 */
function getValidLastLocationPathUsed(): LastLocationPathResult {
  const lastPageRouteUsed = normalizeRoutePath(localStorageService.getItem(storageKeys.routing_lastPageRouteUsed));
  const lastLocationPathUsed = localStorageService.getItem(storageKeys.routing_lastLocationPathUsed);
  const isRouteValid = lastLocationPathUsed !== undefined && doesRoutePathExist(lastPageRouteUsed);

  return isRouteValid ? {isRouteValid, lastLocationPathUsed} : {isRouteValid: false};
}

/**
 * Previously, some routes were stored without a leading slash.
 * This adds the leading slash to any stored route which lacks a leading slash.
 * This allows the previously stored routes (e.g. "courses") to match with the current routes (e.g. "/courses").
 */
function normalizeRoutePath(routePath: string | undefined) {
  return ensureStringBeginsWithSlash(routePath);
}

function ensureStringBeginsWithSlash(input: string | undefined) {
  if (!input) {
    return "/";
  }
  const result = input.startsWith("/") ? input : `/${input}`;
  return result;
}

/**
 * Side effects that should run after render.
 *
 * Broadcast route changed event so we can do things like stop playback should the user leave the lesson page.
 * @param routePath - path route uses to match. e.g. "/lesson/{lessonId}/{segmentId};
 * @param locationPath - current path in browser. e.g. /lesson/123/1
 * @param search - the query/search string from the URL.
 */
function executeSideEffects(
  routePath: string,
  locationPath: string,
  search: string | undefined
) {
  logger.router.debug({
    message: `intercept route`,
    data: [
      { key: `routePath`, value: routePath },
      { key: `locationPath`, value: locationPath },
      { key: `search`, value: search },
    ],
  });

  //let others know that the route has changed (e.g. to stop playing any music, etc)
  routeChanged({
    routePath,
    locationPath,
    search,
    userHasValidToken: !isSignInRequired(),
  });

  // Tell native to handle the route if 1) we are running in a native container and 2) native has told us that it handles the route.
  if (nativeBridge.isRunningInNativeContainer) {
    // Always notify native of route changes so it can make decisions (e.g., skip reload on sign-in page)
    nativeBridge.tellNativeRouteChanged(locationPath, routePath);
    
    if (nativeBridge.routePathsHandledByNative.includes(routePath)) {
      nativeBridge.tellNativeToRouteToRoutePath(routePath);
    } else {
      nativeBridge.tellNativeToRouteToWebApp();
    }
  }

  //store in local storage so that native wrapper can load https://d29h8r0xu3kanz.cloudfront.net/#/last-used-page and load where the user was last.
  if (routePath !== appRoutePaths.lastPageUsed) {
    const locationPathAndSearch = search ? locationPath + search : locationPath;
    setItem(storageKeys.routing_lastLocationPathUsed, locationPathAndSearch);
    setItem(storageKeys.routing_lastPageRouteUsed, routePath);
  }
}

function isSignInNeededToViewPage(routePath: string) {
  const isLastPageUsed = appRoutePaths.lastPageUsed === routePath;
  const pageDoesNotRequireSignIn = isLastPageUsed || isRoutePathOnboardingRelated(routePath);
  if (pageDoesNotRequireSignIn) {
    return false;
  }
  return isSignInRequired();
}

function isRoutePathOnboardingRelated(routePath: string) {
  const onboardingAppRoutePaths = [
    appRoutePaths.onboarding_disclosureAndConsent,
    appRoutePaths.infoPage,
    appRoutePaths.createAccountPage,
    appRoutePaths.createTrialAccountPage,
    appRoutePaths.keyboardWizard,
    appRoutePaths.setupYourKeyboardPage,
    appRoutePaths.setupKeyboardLaterPage,
    appRoutePaths.signInPage, // This page is used both during and after onboarding.
    appRoutePaths.onboarding_setupFeedback_allowMicrophoneAccess,
    appRoutePaths.onboarding_setupFeedback_micAccessNeeded,
    appRoutePaths.onboarding_setupFeedback_youAreAllSet,
    appRoutePaths.onboarding_setupFeedback_setupFeedbackAndScoring,
  ];

  const isRelated = onboardingAppRoutePaths.includes(routePath);
  return isRelated;
}

function doesRoutePathExist(routePath: string | undefined) {
  if (routePath === undefined) {
    return false;
  }
  const appRoutePathValues = Object.values(appRoutePaths);
  return appRoutePathValues.includes(routePath);
}

/**
 * Primarily used by the AppRouter, which intercepts all routes, calling this function, and redirecting to /sign-in when this function returns true.
 */
function isSignInRequired() {
  const signInData = getSignInDataFromLocalStorage();
  if (!signInData) {
    return true;
  }
  if (signInData.version !== currentSignInDataVersion) {
    return true;
  }
  return isTokenExpired(signInData.tokenExpiryTime);
}

/**
 * Currently we assume tokens last for 1 year.
 * @param tokenExpiryTime - epoch ms time when the token will expire.
 */
function isTokenExpired(tokenExpiryTime: number) {
  return tokenExpiryTime < Date.now();
}

export default AppRoute;
